import React, { useEffect, useRef, useState } from 'react';
import './FeaturdProducts.css';
import homeProductsService from '../../../services/homeProducts.service';
import { useNavigate } from 'react-router-dom';

const FeaturedProducts = () => {
  const [scrollProgress, setScrollProgress] = useState({});
  const [featuredSections, setFeaturedSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const sectionsRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    setLoading(true);
    try {
      const result = await homeProductsService.getAllFeaturedProducts();
      if (result.success) {
        // Filter only active products and sort by order
        const activeProducts = result.featured
          .filter(product => product.isActive !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        // Transform to match the existing structure
        const transformedProducts = activeProducts.map(product => ({
          id: product.id,
          productId: product.productId,
          title: product.title,
          description: product.description,
          price: product.price,
          features: product.features || [],
          mainImage: typeof product.images[0] === 'string'
            ? product.images[0]
            : product.images[0]?.url || '',
          images: product.images.slice(1, 5).map(img =>
            typeof img === 'string' ? img : img?.url || ''
          ) || [],
          imagePosition: product.imagePosition || 'left',
          badge: product.badge || ''
        }));

        setFeaturedSections(transformedProducts);
      }
    } catch (error) {
      console.error('Error fetching featured products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      sectionsRef.current.forEach((section, index) => {
        if (section) {
          const rect = section.getBoundingClientRect();
          const windowHeight = window.innerHeight;

          // Calculate progress when section enters viewport
          if (rect.top < windowHeight && rect.bottom > 0) {
            const sectionTop = rect.top;
            const sectionHeight = rect.height;
            const progress = Math.min(
              Math.max((windowHeight - sectionTop) / (windowHeight * 0.7), 0),
              1
            );

            setScrollProgress(prev => ({
              ...prev,
              [index]: progress
            }));
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [featuredSections]);

  // Calculate transforms for each corner image - Sequential animation with staggered delays
  const getImageTransform = (position, progress, imageIndex) => {
    const distance = 140;
    const rotation = 12;

    // Stagger the animation: each image starts at a different point
    const delay = imageIndex * 0.2; // 0, 0.2, 0.4, 0.6
    const adjustedProgress = Math.max(0, Math.min(1, (progress - delay) / (1 - delay)));

    const scale = 0.65 + adjustedProgress * 0.35;
    const opacity = adjustedProgress;
    const zDepth = -30 + (adjustedProgress * 10); // Start behind, come closer

    switch (position) {
      case 'top-left':
        return {
          transform: `translate(${-50 + (-distance * adjustedProgress)}px, ${-50 + (-distance * adjustedProgress)}px) rotate(${-rotation * adjustedProgress}deg) scale(${scale}) translateZ(${zDepth}px)`,
          opacity
        };
      case 'top-right':
        return {
          transform: `translate(${-50 + (distance * adjustedProgress)}px, ${-50 + (-distance * adjustedProgress)}px) rotate(${rotation * adjustedProgress}deg) scale(${scale}) translateZ(${zDepth}px)`,
          opacity
        };
      case 'bottom-left':
        return {
          transform: `translate(${-50 + (-distance * adjustedProgress)}px, ${-50 + (distance * adjustedProgress)}px) rotate(${-rotation * adjustedProgress}deg) scale(${scale}) translateZ(${zDepth}px)`,
          opacity
        };
      case 'bottom-right':
        return {
          transform: `translate(${-50 + (distance * adjustedProgress)}px, ${-50 + (distance * adjustedProgress)}px) rotate(${rotation * adjustedProgress}deg) scale(${scale}) translateZ(${zDepth}px)`,
          opacity
        };
      default:
        return {};
    }
  };

  if (loading) {
    return (
      <section className="featured-showcase">
        <div className="container">
          <div className="featured-showcase__header">
            <h2 className="featured-showcase__title">Collections Vedettes</h2>
            <p className="featured-showcase__subtitle">
              Des produits sélectionnés pour leur qualité exceptionnelle
            </p>
          </div>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p style={{ color: '#64748b' }}>Chargement des produits vedettes...</p>
          </div>
        </div>
      </section>
    );
  }

  if (featuredSections.length === 0) {
    return null; // Don't show the section if there are no active products
  }

  return (
    <section className="featured-showcase">
      <div className="container">
        <div className="featured-showcase__header">
          <h2 className="featured-showcase__title">Collections Vedettes</h2>
          <p className="featured-showcase__subtitle">
            Des produits sélectionnés pour leur qualité exceptionnelle
          </p>
        </div>

        {featuredSections.map((section, index) => (
          <div
            key={section.id}
            ref={el => sectionsRef.current[index] = el}
            className={`featured-section ${section.imagePosition === 'right' ? 'featured-section--reverse' : ''}`}
          >
            {/* Badge */}
            {section.badge && (
              <div className="featured-badge">{section.badge}</div>
            )}

            {/* Image Side */}
            <div className="featured-section__images">
              <div className="images-stack">
                {/* Main Center Image */}
                <div className="image-main">
                  <img src={section.mainImage} alt={section.title} />
                </div>

                {/* Secondary Images - Only render if they exist */}
                {section.images[0] && (
                  <div
                    className="image-secondary image-secondary--top-left"
                    style={getImageTransform('top-left', scrollProgress[index] || 0, 0)}
                  >
                    <img src={section.images[0]} alt="Vue détail 1" />
                  </div>
                )}

                {section.images[1] && (
                  <div
                    className="image-secondary image-secondary--top-right"
                    style={getImageTransform('top-right', scrollProgress[index] || 0, 1)}
                  >
                    <img src={section.images[1]} alt="Vue détail 2" />
                  </div>
                )}

                {section.images[2] && (
                  <div
                    className="image-secondary image-secondary--bottom-left"
                    style={getImageTransform('bottom-left', scrollProgress[index] || 0, 2)}
                  >
                    <img src={section.images[2]} alt="Vue détail 3" />
                  </div>
                )}

                {section.images[3] && (
                  <div
                    className="image-secondary image-secondary--bottom-right"
                    style={getImageTransform('bottom-right', scrollProgress[index] || 0, 3)}
                  >
                    <img src={section.images[3]} alt="Vue détail 4" />
                  </div>
                )}
              </div>
            </div>

            {/* Content Side */}
            <div className="featured-section__content">
              <h3 className="featured-section__title">{section.title}</h3>
              <p className="featured-section__description">{section.description}</p>

              {section.features && section.features.length > 0 && (
                <div className="featured-section__features">
                  {section.features.map((feature, idx) => (
                    <div key={idx} className="feature-item">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="featured-section__footer">
                <span className="featured-section__price">{section.price}</span>
                <button
                  className="featured-section__btn"
                  onClick={() => navigate(`/product/${section.productId}`)}
                >
                  Découvrir
                  <span className="btn-arrow">→</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FeaturedProducts;